/**
 * Cut model: the editing script's `cuts` are time ranges (ms, clip/source time)
 * removed from the output. Every other timing in the script stays in source
 * time; this module is the single mapping between source time and output time.
 */
export interface CutRange {
  startMs: number
  endMs: number
}

/** Sorted, merged (overlapping or touching), clamped to [0, durationMs], empty ranges dropped. */
export function normalizeCuts(cuts: CutRange[] | undefined, durationMs = Infinity): CutRange[] {
  const sorted = (cuts ?? [])
    .map((cut) => ({ startMs: Math.max(0, cut.startMs), endMs: Math.min(durationMs, cut.endMs) }))
    .filter((cut) => cut.endMs > cut.startMs)
    .sort((left, right) => left.startMs - right.startMs)
  const merged: CutRange[] = []
  for (const cut of sorted) {
    const last = merged[merged.length - 1]
    if (last && cut.startMs <= last.endMs) {
      last.endMs = Math.max(last.endMs, cut.endMs)
    } else {
      merged.push({ ...cut })
    }
  }
  return merged
}

/** Adds ranges to the cut list. */
export function addCuts(cuts: CutRange[] | undefined, added: CutRange[]): CutRange[] {
  return normalizeCuts([...(cuts ?? []), ...added])
}

/** Removes (restores) a range from the cut list, splitting cuts that straddle it. */
export function restoreRange(cuts: CutRange[] | undefined, restored: CutRange): CutRange[] {
  const result: CutRange[] = []
  for (const cut of normalizeCuts(cuts)) {
    if (cut.endMs <= restored.startMs || cut.startMs >= restored.endMs) {
      result.push(cut)
      continue
    }
    if (cut.startMs < restored.startMs) result.push({ startMs: cut.startMs, endMs: restored.startMs })
    if (cut.endMs > restored.endMs) result.push({ startMs: restored.endMs, endMs: cut.endMs })
  }
  return result
}

export function isCutAt(ms: number, cuts: CutRange[] | undefined): boolean {
  return (cuts ?? []).some((cut) => ms >= cut.startMs && ms < cut.endMs)
}

/** True when every part of `range` is cut. */
export function isRangeCut(range: CutRange, cuts: CutRange[] | undefined): boolean {
  return normalizeCuts(cuts).some((cut) => cut.startMs <= range.startMs && cut.endMs >= range.endMs)
}

export function outputDurationMs(durationMs: number, cuts: CutRange[] | undefined): number {
  const removed = normalizeCuts(cuts, durationMs).reduce((sum, cut) => sum + cut.endMs - cut.startMs, 0)
  return durationMs - removed
}

/** Source time -> output time. A time inside a cut maps to where the cut was spliced. */
export function sourceToOutputMs(ms: number, cuts: CutRange[] | undefined): number {
  let removed = 0
  for (const cut of normalizeCuts(cuts)) {
    if (ms <= cut.startMs) break
    removed += Math.min(ms, cut.endMs) - cut.startMs
  }
  return ms - removed
}

/** Output time -> source time. The splice point maps to the first kept source time after the cut. */
export function outputToSourceMs(ms: number, cuts: CutRange[] | undefined): number {
  let source = ms
  for (const cut of normalizeCuts(cuts)) {
    if (source < cut.startMs) break
    source += cut.endMs - cut.startMs
  }
  return source
}

export interface KeptSpan {
  /** First output frame of the span. */
  fromFrame: number
  /** First source frame shown by the span. */
  sourceFrame: number
  durationInFrames: number
}

/** Composition length in frames once cuts are applied (rounded up, like the uncut composition). */
export function outputDurationInFrames(durationMs: number, cuts: CutRange[] | undefined, fps: number): number {
  return Math.max(1, Math.ceil((outputDurationMs(durationMs, cuts) / 1000) * fps))
}

/**
 * The kept parts of the clip as frame spans. Boundaries are rounded once each,
 * so span durations always add up to outputDurationInFrames.
 */
export function keptSpans(durationMs: number, cuts: CutRange[] | undefined, fps: number): KeptSpan[] {
  const toFrame = (ms: number) => Math.round((ms / 1000) * fps)
  const spans: KeptSpan[] = []
  let sourceStart = 0
  for (const cut of [...normalizeCuts(cuts, durationMs), { startMs: durationMs, endMs: durationMs }]) {
    if (cut.startMs > sourceStart) {
      const fromFrame = toFrame(sourceToOutputMs(sourceStart, cuts))
      const toOut = cut.startMs === durationMs
        ? outputDurationInFrames(durationMs, cuts, fps)
        : toFrame(sourceToOutputMs(cut.startMs, cuts))
      if (toOut > fromFrame) {
        spans.push({ fromFrame, sourceFrame: toFrame(sourceStart), durationInFrames: toOut - fromFrame })
      }
    }
    sourceStart = cut.endMs
  }
  return spans
}

interface CaptionElementLike {
  timeline: { in: number; out: number }
  deleted?: boolean
}

/**
 * Marks caption words inside cuts as deleted so a caption group never shows a
 * cut word next to kept ones. Caption elements span the word's start to its end,
 * so a word belongs to the cut containing its midpoint.
 */
export function dropCutCaptionWords<T extends { trackType: string; sections: Array<{ segments: Array<Record<string, any>> }> }>(
  tracks: T[],
  cuts: CutRange[] | undefined,
): T[] {
  if (!cuts?.length) return tracks
  return tracks.map((track) => track.trackType !== "CaptionTrack" ? track : {
    ...track,
    sections: track.sections.map((section) => ({
      ...section,
      segments: section.segments.map((segment) => {
        const elements = segment.content?.textElements as CaptionElementLike[] | undefined
        if (!elements) return segment
        return {
          ...segment,
          content: {
            ...segment.content,
            textElements: elements.map((element) =>
              isCutAt((element.timeline.in + element.timeline.out) / 2, cuts) ? { ...element, deleted: true } : element,
            ),
          },
        }
      }),
    })),
  })
}
